import React from "react";
import { Row, Col, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import type { SettingKey } from "./HierarchyManagementConstants";
import TimezoneSelect from "../components/TimezoneSelect";
import type { WizardData } from "./HierarchyManagement";

interface SettingsFormProps {
  settings: readonly SettingKey[];
  wizardData: WizardData;
  setWizardData: React.Dispatch<React.SetStateAction<WizardData>>;
  override: Record<string, boolean>;
  setOverride: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  prefix?: "location" | "company";
  parentData?: Record<string, any>;
  showOverride?: boolean;
}

const SettingsForm: React.FC<SettingsFormProps> = ({
  settings,
  wizardData,
  setWizardData,
  override,
  setOverride,
  prefix = "",
  parentData = {},
  showOverride = true,
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'settingsForm' });

  const PPE_OPTIONS = [
    { label: t('helmet'), value: 'helmet' },
    { label: t('hairnetGloves'), value: 'hairnet_gloves' },
    { label: t('industrialGloves'), value: 'industrial_gloves' },
    { label: t('industrialJacket'), value: 'industrial_jacket' },
  ];
  const getInheritedValue = (s: SettingKey, data: WizardData = wizardData) => {
    return parentData[s] ?? data[s as keyof WizardData];
  };

  const getValue = (s: SettingKey) => {
    if (prefix && override[s]) {
      return wizardData[`${prefix}_${s}` as keyof WizardData];
    }
    return getInheritedValue(s);
  };

  const handleChange = (s: SettingKey, value: any) => {
    const key = prefix ? `${prefix}_${s}` : s;
    setWizardData((d) => {
      const next = { ...d, [key]: value } as Record<string, any>;
      if (s === "shifts_enabled" && value === false) {
        next[prefix ? `${prefix}_shift_auto_detect` : "shift_auto_detect"] = false;
      }
      return next as WizardData;
    });
  };

  const handleOverrideChange = (s: SettingKey, checked: boolean) => {
    setOverride((o) => {
      const next = { ...o, [s]: checked };
      if (s === "shifts_enabled" && !checked) {
        next.shift_auto_detect = false;
      }
      return next;
    });

    const isBooleanSetting = s === "shifts_enabled" || s === "shift_auto_detect" || s === "payroll_enabled";
    if (prefix) {
      setWizardData((d) => {
        const key = `${prefix}_${s}`;
        const next = { ...d } as Record<string, any>;
        if (checked) {
          next[key] = isBooleanSetting ? false : (d[key as keyof typeof d] ?? parentData[s] ?? d[s as keyof typeof d]);
        } else {
          next[key] = getInheritedValue(s, d);
          if (s === "shifts_enabled") {
            next[`${prefix}_shift_auto_detect`] = getInheritedValue("shift_auto_detect", d);
          }
        }
        return next as WizardData;
      });
    } else if (checked && isBooleanSetting) {
      setWizardData((d) => ({ ...d, [s]: d[s as keyof typeof d] ?? false }));
    }
  };

  return (
    <Row className="g-3 an-settings-form">
      {settings.map((s) => {
        if (s === "shift_auto_detect" && !getValue("shifts_enabled")) return null;

        return (
        <Col md={6} key={s}>
          <Form.Group className="an-settings-field">
            <Form.Label className="small fw-bold text-muted text-uppercase mb-1">
              {t(s)}{" "}
              {!override[s] && prefix && (
                <span
                  className="an-settings-inherited-label"
                >
                  {t('inherited')}
                </span>
              )}
            </Form.Label>
            <div className="d-flex align-items-center">
              {s === "timezone" ? (
                <div className="flex-grow-1">
                  <TimezoneSelect
                    value={getValue(s) as string || ""}
                    onChange={(v) => handleChange(s, v)}
                    disabled={prefix ? !override[s] : false}
                  />
                </div>
              ) : s === "ppe_types" ? (
                <div className="flex-grow-1">
                  <div className="d-flex flex-wrap gap-2 overflow-auto an-settings-checkbox-grid">
                    {PPE_OPTIONS.map(option => (
                      <Form.Check
                        key={option.value}
                        type="checkbox"
                        id={`ppe-${prefix}-${s}-${option.value}`}
                        label={<span className="small">{option.label}</span>}
                        checked={(getValue(s) as string[])?.includes(option.value) || false}
                        onChange={(e) => {
                          const currentTypes = (getValue(s) as string[]) || [];
                          let nextTypes;
                          if (e.target.checked) {
                            nextTypes = [...currentTypes, option.value];
                          } else {
                            nextTypes = currentTypes.filter(t => t !== option.value);
                          }
                          handleChange(s, nextTypes.length > 0 ? nextTypes : []);
                        }}
                        disabled={prefix ? !override[s] : false}
                      />
                    ))}
                  </div>
                </div>
              ) : s === "shifts_enabled" || s === "shift_auto_detect" || s === "payroll_enabled" ? (
                <div className="flex-grow-1">
                  <Form.Check
                    type="switch"
                    id={`switch-${prefix}-${s}`}
                    label={getValue(s) ? t('enabled', { ns: 'common', keyPrefix: 'status' }) : t('disabled', { ns: 'common', keyPrefix: 'status' })}
                    checked={!!getValue(s)}
                    disabled={prefix ? !override[s] : false}
                    onChange={(e) => handleChange(s, e.target.checked)}
                  />
                </div>
              ) : (
                <Form.Control
                  type={
                    s.includes("_th") ||
                    s.includes("_from") ||
                    s.includes("_to") ||
                    s === "similarity_addition" ||
                    s.includes("confidence")
                      ? "number"
                      : "text"
                  }
                  step={
                    s.includes("_th") ||
                    s.includes("_from") ||
                    s.includes("_to") ||
                    s === "similarity_addition" ||
                    s.includes("confidence")
                      ? "0.01"
                      : undefined
                  }
                  min={s.includes("confidence") ? 0 : undefined}
                  max={s.includes("confidence") ? 1 : undefined}
                  value={getValue(s) ?? ""}
                  disabled={prefix ? !override[s] : false}
                  onChange={(e) => {
                    const isNumeric =
                      s.includes("_th") ||
                      s.includes("_from") ||
                      s.includes("_to") ||
                      s === "similarity_addition" ||
                      s.includes("confidence");
                    const val = isNumeric
                      ? e.target.value === ""
                        ? undefined
                        : Number(e.target.value)
                      : e.target.value;
                    handleChange(s, val);
                  }}
                />
              )}
              {showOverride && prefix && (
                <Form.Check
                  className="ms-2"
                  type="switch"
                  label={<span className="small text-muted">{t('override')}</span>}
                  checked={override[s] || false}
                  onChange={(e) => handleOverrideChange(s, e.target.checked)}
                />
              )}
            </div>
          </Form.Group>
        </Col>
        );
      })}
    </Row>
  );
};

export default SettingsForm;